(function () {
  'use strict';

  if (window.theme && window.theme.productCollectionShowcaseLoaded) return;
  window.theme = window.theme || {};
  window.theme.productCollectionShowcaseLoaded = true;

  const colorwayDataCache = new Map();

  function initSection(section) {
    if (!section || !section.hasAttribute('data-product-collection-showcase-section')) return;

    const collectionHandleValue = section.dataset.collectionHandle || '';
    if (!collectionHandleValue) return;

    const productLimit = parseInt(section.dataset.productLimit, 10) || 4;
    const showBadge = section.dataset.showBadge !== 'false';
    const imageLoading = section.dataset.imageLoading === 'lazy' ? 'lazy' : 'eager';
    const defaultProductsShownOnDesktop =
      parseFloat(section.dataset.defaultProductsShownOnDesktop) || 4;

    const mainSwiperElement = section.querySelector('[data-product-collection-swiper]');
    const productWrapperElement = section.querySelector('[data-product-wrapper]');
    const paginationEl = section.querySelector('.product-collection-showcase__pagination-wrap .swiper-pagination');
    const progressBar = section.querySelector('[data-progress-bar]');
    const progressBarFill = progressBar
      ? progressBar.querySelector('.product-collection-showcase__progress-bar')
      : null;
    let mainSwiperInstance = null;
    let resizeDebounceTimer = null;
    let wheelListenerAdded = false;
    let isWheelScrolling = false;
    let accumulatedWheelDelta = 0;
    let wheelRafId = null;
    const isCollectionDrops = section.classList.contains('collection-drops');
    const isCartDrawerShowcase = section.classList.contains('product-collection-showcase--cart-drawer');
    const showcaseNavPrev = section.querySelector('[data-showcase-prev]');
    const showcaseNavNext = section.querySelector('[data-showcase-next]');
    const showcaseNavigation =
      showcaseNavPrev && showcaseNavNext
        ? { prevEl: showcaseNavPrev, nextEl: showcaseNavNext }
        : false;
    const collectionDropsBreakpoints = {
      640: { slidesPerView: 2.2, spaceBetween: 0 },
      1024: { slidesPerView: 3.2, spaceBetween: 0 },
      1280: { slidesPerView: defaultProductsShownOnDesktop + 0.45, spaceBetween: 0 },
    };

    const updateProgressBar = (swiper, progressOverride) => {
      if (!progressBarFill || !swiper) return;
      if (isWheelScrolling && progressOverride === undefined) return;
      const thumbWidthPercent = 20;
      const maxLeft = 100 - thumbWidthPercent;
      const progress =
        progressOverride !== undefined
          ? Math.min(1, Math.max(0, progressOverride))
          : Math.min(1, Math.max(0, swiper.progress));
      const leftPct = progress * maxLeft;
      progressBarFill.style.left = leftPct + '%';
    };

    const setupProgressBarDrag = (progressBarEl, swiper) => {
      if (!progressBarEl || !swiper) return;
      if (progressBarEl._progressDragSetup) {
        progressBarEl._progressSwiper = swiper;
        return;
      }
      progressBarEl._progressDragSetup = true;
      progressBarEl._progressSwiper = swiper;

      function getProgress(clientX) {
        const rect = progressBarEl.getBoundingClientRect();
        const progress = (clientX - rect.left) / rect.width;
        return Math.min(1, Math.max(0, progress));
      }

      function goToProgress(clientX) {
        const total = progressBarEl._progressSwiper ? progressBarEl._progressSwiper.slides.length : 0;
        if (total <= 1) return;
        const slideIndex = Math.min(total - 1, Math.max(0, Math.round(getProgress(clientX) * (total - 1))));
        progressBarEl._progressSwiper.slideTo(slideIndex);
        updateProgressBar(progressBarEl._progressSwiper);
      }

      let activePointerId = null;

      function onPointerMove(e) {
        if (e.pointerType === 'touch') e.preventDefault();
        goToProgress(e.clientX);
      }

      function endDrag(e) {
        if (activePointerId !== null && e.pointerId !== activePointerId) return;
        activePointerId = null;
        progressBarEl.classList.remove('is-dragging');
        try {
          progressBarEl.releasePointerCapture(e.pointerId);
        } catch (err) {}
        progressBarEl.removeEventListener('pointermove', onPointerMove);
        progressBarEl.removeEventListener('pointerup', endDrag);
        progressBarEl.removeEventListener('pointercancel', endDrag);
        progressBarEl.removeEventListener('pointerleave', onPointerLeave);
      }

      function onPointerLeave(e) {
        if (activePointerId !== null && e.pointerId === activePointerId) endDrag(e);
      }

      progressBarEl.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.cancelable) e.preventDefault();
          activePointerId = e.pointerId;
          progressBarEl.classList.add('is-dragging');
          goToProgress(e.clientX);
          progressBarEl.setPointerCapture(e.pointerId);
          progressBarEl.addEventListener('pointermove', onPointerMove, { passive: false });
          progressBarEl.addEventListener('pointerup', endDrag);
          progressBarEl.addEventListener('pointercancel', endDrag);
          progressBarEl.addEventListener('pointerleave', onPointerLeave);
        },
        { passive: false },
      );
    };

    const flattenGraphQLConnections = (graphQLObject) => {
      if (!(graphQLObject && typeof graphQLObject === 'object') || Array.isArray(graphQLObject)) return graphQLObject;
      if ('edges' in graphQLObject && Array.isArray(graphQLObject.edges)) {
        return graphQLObject.edges.map((edgeItem) => flattenGraphQLConnections(edgeItem.node));
      }
      for (const propertyName in graphQLObject)
        graphQLObject[propertyName] = flattenGraphQLConnections(graphQLObject[propertyName]);
      return graphQLObject;
    };

    /**
     * Storefront GraphQL returns catalog/retail prices and ignores logged-in
     * wholesale/B2B customer context. Overlay session-aware prices from the
     * Ajax Product API (same source Liquid uses on PDPs / YMAL).
     */
    const centsToAmountString = (cents) => (Number(cents || 0) / 100).toFixed(2);

    const mapOptionsFromProductJson = (productJson) => {
      return (productJson.options || []).map((optionItem) => ({
        name: typeof optionItem === 'string' ? optionItem : optionItem.name || 'Size',
        values: typeof optionItem === 'string' ? [] : optionItem.values || [],
      }));
    };

    const mapVariantsFromProductJson = (productJson) => {
      const options = productJson.options || [];

      const optionNameAt = (index) => {
        if (!options[index]) return 'Option' + (index + 1);
        return typeof options[index] === 'string' ? options[index] : options[index].name || 'Option' + (index + 1);
      };

      return (productJson.variants || []).map((variantJson) => {
        const selectedOptions = [];

        if (variantJson.option1) {
          selectedOptions.push({ name: optionNameAt(0), value: variantJson.option1 });
        }
        if (variantJson.option2) {
          selectedOptions.push({ name: optionNameAt(1), value: variantJson.option2 });
        }
        if (variantJson.option3) {
          selectedOptions.push({ name: optionNameAt(2), value: variantJson.option3 });
        }

        return {
          id: 'gid://shopify/ProductVariant/' + variantJson.id,
          title: variantJson.title,
          availableForSale: variantJson.available !== false,
          available: variantJson.available !== false,
          price: {
            amount: String(variantJson.price),
            currencyCode: 'USD',
          },
          image: variantJson.featured_image
            ? {
                url: variantJson.featured_image.src || variantJson.featured_image,
                width: variantJson.featured_image.width,
                height: variantJson.featured_image.height,
              }
            : null,
          selectedOptions,
          option1: variantJson.option1 || null,
          option2: variantJson.option2 || null,
          option3: variantJson.option3 || null,
        };
      });
    };

    const mergeProductJsonCatalogData = (product, productJson) => {
      if (!product || !productJson) return product;

      if (productJson.options?.length) {
        product.options = mapOptionsFromProductJson(productJson);
      }

      if (productJson.variants?.length) {
        product.variants = mapVariantsFromProductJson(productJson);
      }

      if (typeof productJson.tags === 'string') {
        product.tags = productJson.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
      } else if (Array.isArray(productJson.tags)) {
        product.tags = productJson.tags;
      }

      return product;
    };

    const applyCustomerAwarePricing = (graphqlProduct, productJson) => {
      if (!graphqlProduct || !productJson) return graphqlProduct;

      mergeProductJsonCatalogData(graphqlProduct, productJson);

      const currencyCode =
        graphqlProduct.priceRange?.minVariantPrice?.currencyCode ||
        (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) ||
        'USD';

      const priceMinCents =
        productJson.price_min != null ? productJson.price_min : productJson.price;
      const priceMinAmount = centsToAmountString(priceMinCents);

      graphqlProduct.priceRange = {
        minVariantPrice: {
          amount: priceMinAmount,
          currencyCode,
        },
      };

      const compareAtMinCents =
        productJson.compare_at_price_min != null
          ? productJson.compare_at_price_min
          : productJson.compare_at_price;

      if (compareAtMinCents != null && Number(compareAtMinCents) > Number(priceMinCents)) {
        graphqlProduct.compareAtPriceRange = {
          minVariantPrice: {
            amount: centsToAmountString(compareAtMinCents),
            currencyCode,
          },
        };
      } else {
        graphqlProduct.compareAtPriceRange = {
          minVariantPrice: {
            amount: '0',
            currencyCode,
          },
        };
      }

      if (Array.isArray(graphqlProduct.variants) && Array.isArray(productJson.variants)) {
        const priceByVariantId = new Map();
        productJson.variants.forEach((variantJson) => {
          priceByVariantId.set(String(variantJson.id), variantJson.price);
        });

        graphqlProduct.variants.forEach((variant) => {
          const variantId =
            typeof variant.id === 'string' && variant.id.includes('/')
              ? variant.id.split('/').pop()
              : String(variant.id);
          if (!priceByVariantId.has(variantId)) return;
          variant.price = {
            amount: centsToAmountString(priceByVariantId.get(variantId)),
            currencyCode,
          };
        });
      }

      return graphqlProduct;
    };

    const hydrateProductsWithCustomerPrices = async (products) => {
      if (!products?.length) return products;

      return Promise.all(
        products.map(async (product) => {
          if (!product?.handle) return product;
          try {
            const response = await fetch(`/products/${product.handle}.js`);
            if (!response.ok) return product;
            const productJson = await response.json();
            return applyCustomerAwarePricing(product, productJson);
          } catch (error) {
            return product;
          }
        }),
      );
    };

    const fetchCollectionProductsFromJson = async (handle, limit) => {
      try {
        const jsonResponse = await fetch(
          '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit,
        );
        if (!jsonResponse.ok) return [];

        const jsonData = await jsonResponse.json();
        const rawProducts = jsonData.products || [];
        if (rawProducts.length === 0) return [];

        const optionName = (opts, index) => {
          if (!opts || !opts[index]) return 'Option' + (index + 1);
          const option = opts[index];
          return typeof option === 'string' ? option : option.name || 'Option' + (index + 1);
        };

        const products = rawProducts.map((productItem) => {
          const options = productItem.options || [];
          const variants = (productItem.variants || []).map((variantItem) => {
            const selectedOptions = [];
            if (variantItem.option1) {
              selectedOptions.push({ name: optionName(options, 0), value: variantItem.option1 });
            }
            if (variantItem.option2) {
              selectedOptions.push({ name: optionName(options, 1), value: variantItem.option2 });
            }
            if (variantItem.option3) {
              selectedOptions.push({ name: optionName(options, 2), value: variantItem.option3 });
            }

            return {
              id: 'gid://shopify/ProductVariant/' + variantItem.id,
              title: variantItem.title,
              availableForSale: variantItem.available !== false,
              price: { amount: String(variantItem.price), currencyCode: 'USD' },
              image: variantItem.image
                ? {
                    url: variantItem.image.src,
                    width: variantItem.image.width,
                    height: variantItem.image.height,
                  }
                : null,
              selectedOptions,
            };
          });

          const prices = variants
            .filter((variantItem) => variantItem.price && variantItem.price.amount)
            .map((variantItem) => parseFloat(variantItem.price.amount));
          const comparePrices = (productItem.variants || []).map((variantItem) =>
            variantItem.compare_at_price ? parseFloat(variantItem.compare_at_price) : 0,
          );
          const minPrice = prices.length ? Math.min(...prices) : 0;
          const maxCompare = comparePrices.length ? Math.max(...comparePrices) : 0;
          const hasCompare = maxCompare > minPrice && maxCompare > 0;

          return {
            id:
              typeof productItem.id === 'string' && productItem.id.includes('/')
                ? productItem.id
                : 'gid://shopify/Product/' + productItem.id,
            handle: productItem.handle,
            title: productItem.title,
            tags: Array.isArray(productItem.tags)
              ? productItem.tags
              : String(productItem.tags || '')
                  .split(',')
                  .map((tag) => tag.trim())
                  .filter(Boolean),
            options: mapOptionsFromProductJson(productItem),
            priceRange: { minVariantPrice: { amount: String(minPrice), currencyCode: 'USD' } },
            compareAtPriceRange: hasCompare
              ? { minVariantPrice: { amount: String(maxCompare), currencyCode: 'USD' } }
              : null,
            images: (productItem.images || []).map((imageItem) => ({
              url: imageItem.src,
              altText: imageItem.alt || productItem.title,
              width: imageItem.width,
              height: imageItem.height,
            })),
            variants,
          };
        });

        return hydrateProductsWithCustomerPrices(products.slice(0, limit));
      } catch (error) {
        return [];
      }
    };

    const fetchCollectionProducts = async () => {
      let graphQLProducts = [];

      try {
        const countryCode = section.dataset.countryCode || null;
        const apiResponse = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
          },
          body: JSON.stringify({
            query: countryCode
              ? `
            query getCollectionProducts($handle: String!, $first: Int!, $country: CountryCode!) @inContext(country: $country) {
              collection(handle: $handle) {
                products(first: $first) {
                  edges {
                    node {
                      id
                      handle
                      title
                      tags
                      options { name values }
                      priceRange {
                        minVariantPrice { amount currencyCode }
                      }
                      compareAtPriceRange {
                        minVariantPrice { amount currencyCode }
                      }
                      images(first: 10) {
                        edges {
                          node {
                            url(transform: {maxWidth: 600, maxHeight: 800})
                            width height altText
                          }
                        }
                      }
                      variants(first: 250) {
                        edges {
                          node {
                            id title availableForSale
                            price { amount currencyCode }
                            image {
                              url(transform: {maxWidth: 100, maxHeight: 100})
                              width height
                            }
                            selectedOptions { name value }
                            metafield(namespace: "custom", key: "swatch_image") { value }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `
              : `
            query getCollectionProducts($handle: String!, $first: Int!) {
              collection(handle: $handle) {
                products(first: $first) {
                  edges {
                    node {
                      id
                      handle
                      title
                      tags
                      options { name values }
                      priceRange {
                        minVariantPrice { amount currencyCode }
                      }
                      compareAtPriceRange {
                        minVariantPrice { amount currencyCode }
                      }
                      images(first: 10) {
                        edges {
                          node {
                            url(transform: {maxWidth: 600, maxHeight: 800})
                            width height altText
                          }
                        }
                      }
                      variants(first: 250) {
                        edges {
                          node {
                            id title availableForSale
                            price { amount currencyCode }
                            image {
                              url(transform: {maxWidth: 100, maxHeight: 100})
                              width height
                            }
                            selectedOptions { name value }
                            metafield(namespace: "custom", key: "swatch_image") { value }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
            variables: countryCode
              ? { handle: collectionHandleValue, first: productLimit, country: countryCode }
              : { handle: collectionHandleValue, first: productLimit },
          }),
        });

        const responseData = await apiResponse.json();
        if (responseData?.data?.collection?.products) {
          const flattenedProducts = flattenGraphQLConnections(responseData.data.collection.products);
          graphQLProducts = await hydrateProductsWithCustomerPrices(flattenedProducts.slice(0, productLimit));
        }
      } catch (error) {
        graphQLProducts = [];
      }

      if (graphQLProducts.length >= productLimit) {
        return graphQLProducts;
      }

      const jsonProducts = await fetchCollectionProductsFromJson(collectionHandleValue, productLimit);
      if (jsonProducts.length > graphQLProducts.length) {
        return jsonProducts;
      }

      return graphQLProducts;
    };

    const validateImageUrl = (imageUrl) => {
      if (!imageUrl) return Promise.resolve(false);
      const timeoutMs = 4000;
      return (async () => {
        try {
          const c = new AbortController();
          const t = setTimeout(() => c.abort(), timeoutMs);
          const res = await fetch(imageUrl, { method: 'GET', signal: c.signal, credentials: 'omit' });
          clearTimeout(t);
          if (res.status === 404) return false;
          if (!res.ok) return false;
          const ct = (res.headers.get('Content-Type') || '').toLowerCase();
          if (ct && !ct.startsWith('image/')) return false;
          return true;
        } catch (e) {
          try {
            return await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
              img.src = imageUrl;
              setTimeout(() => resolve(false), timeoutMs);
            });
          } catch (err) {
            return false;
          }
        }
      })();
    };

    const extractSwatchImageFromVariant = (variantData) => {
      if (!variantData) return null;

      let swatchImageValue = null;

      if (variantData.metafield) {
        if (variantData.metafield.value !== null && variantData.metafield.value !== undefined) {
          swatchImageValue = variantData.metafield.value;
        } else if (typeof variantData.metafield === 'string') {
          swatchImageValue = variantData.metafield;
        }
      } else if (variantData.metafields && variantData.metafields.swatch_image) {
        swatchImageValue = variantData.metafields.swatch_image;
      }

      if (swatchImageValue !== null && swatchImageValue !== undefined) {
        const trimmedSwatchValue = String(swatchImageValue).trim();
        if (
          trimmedSwatchValue &&
          trimmedSwatchValue.length > 0 &&
          trimmedSwatchValue !== 'null' &&
          trimmedSwatchValue !== 'undefined' &&
          trimmedSwatchValue !== '' &&
          (trimmedSwatchValue.startsWith('http://') || trimmedSwatchValue.startsWith('https://'))
        ) {
          return trimmedSwatchValue;
        }
      }
      return null;
    };

    const isStandardVariant = (variant) => {
      if (!variant) return false;
      const titleDowncase = (variant.title || '').toLowerCase();
      if (titleDowncase.includes('standard')) return true;
      const options = variant.selectedOptions || [];
      for (let i = 0; i < options.length; i++) {
        if ((options[i].value || '').toLowerCase() === 'standard') return true;
      }
      return false;
    };

    const getVariantSwatchOrder = (variants) => {
      if (!variants || !variants.length) return [];
      const standard = variants.find(isStandardVariant);
      if (standard) {
        const rest = variants.filter((v) => v !== standard);
        return [standard, ...rest];
      }
      return [...variants];
    };

    const getFirstValidSwatchUrl = async (colorwayProductItem) => {
      const variants = colorwayProductItem.variants || [];
      const ordered = getVariantSwatchOrder(variants);
      for (const variant of ordered) {
        const metafieldUrl = extractSwatchImageFromVariant(variant);
        if (metafieldUrl && (await validateImageUrl(metafieldUrl))) return metafieldUrl;
      }
      for (const variant of ordered) {
        if (variant?.image?.url && (await validateImageUrl(variant.image.url))) return variant.image.url;
      }
      return null;
    };

    const extractColorTagFromProduct = (productData) => {
      if (!productData.tags || !Array.isArray(productData.tags)) return null;

      for (const productTag of productData.tags) {
        if (productTag.includes('_') && productTag !== 'new_arrival' && productTag !== 'Endless_Summer') {
          return productTag;
        }
      }
      return null;
    };

    const fetchColorwayProductsByTag = async (colorTag) => {
      if (colorwayDataCache.has(colorTag)) {
        return colorwayDataCache.get(colorTag);
      }

      try {
        const colorwayApiResponse = await fetch('/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
          },
          body: JSON.stringify({
            query: `
            query getProductsByTag($query: String!) {
              colorways: products(query: $query, first: 50) {
                edges {
                  node {
                    handle
                    title
                    images(first: 2) {
                      edges {
                        node {
                          width
                          height
                          url(transform: {maxHeight: 100, maxWidth: 100})
                        }
                      }
                    }
                    variants(first: 250) {
                      edges {
                        node {
                          title
                          availableForSale
                          image {
                            url(transform: {maxHeight: 100, maxWidth: 100})
                            width
                            height
                          }
                          selectedOptions {
                            name
                            value
                          }
                          metafield(namespace: "custom", key: "swatch_image") {
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
            variables: { query: `tag:${colorTag}` },
          }),
        });

        const colorwayResponseData = await colorwayApiResponse.json();
        const flattenedColorwayProducts = flattenGraphQLConnections(colorwayResponseData?.data?.colorways || []);

        colorwayDataCache.set(colorTag, flattenedColorwayProducts);
        return flattenedColorwayProducts;
      } catch (error) {
        return [];
      }
    };

    const getProductColorOptions = async (productData) => {
      try {
        const productColorTag = extractColorTagFromProduct(productData);
        if (!productColorTag) return [];

        const colorwayProductsList = await fetchColorwayProductsByTag(productColorTag);
        if (colorwayProductsList.length === 0) return [];

        const uniqueColorOptionsMap = new Map();

        const colorwayProcessingPromises = colorwayProductsList.map(async (colorwayProductItem) => {
          let fallbackImageUrl = null;
          if (colorwayProductItem.variants?.length) {
            const v = colorwayProductItem.variants.find((x) => x.image?.url);
            fallbackImageUrl = v?.image?.url || colorwayProductItem.variants[0]?.image?.url;
          }
          if (!fallbackImageUrl && colorwayProductItem.images?.[0]?.url) {
            fallbackImageUrl = colorwayProductItem.images[0].url;
          }

          const swatchImageUrl = await getFirstValidSwatchUrl(colorwayProductItem);
          const finalImage = swatchImageUrl || fallbackImageUrl;
          const finalFallback = fallbackImageUrl || finalImage;

          return {
            name: colorwayProductItem.title,
            handle: colorwayProductItem.handle,
            image: finalImage,
            fallback: finalFallback,
            available: colorwayProductItem.variants?.some((variantItem) => variantItem.availableForSale) || false,
          };
        });

        const processedColorOptions = await Promise.all(colorwayProcessingPromises);

        processedColorOptions.forEach((colorOption) => {
          if (!uniqueColorOptionsMap.has(colorOption.handle)) {
            uniqueColorOptionsMap.set(colorOption.handle, colorOption);
          }
        });

        return Array.from(uniqueColorOptionsMap.values());
      } catch (error) {
        return [];
      }
    };

    const formatProductPrice = (priceAmount, currencyCodeValue) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCodeValue || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(parseFloat(priceAmount));
    };

    const renderProductCardHTML = async (productData, colorOptionsList = []) => {
      if (typeof window.renderProductShowcaseCard === 'function') {
        const productCardHTML = window.renderProductShowcaseCard(
          productData,
          colorOptionsList,
          'product-collection-showcase',
          showBadge,
          true,
          false,
          imageLoading,
        );
        return `<div class="swiper-slide">${productCardHTML}</div>`;
      }

      return '';
    };

    const initializeProductImageSwipers = () => {
      if (typeof window.initProductImageSwipers !== 'function') {
        setTimeout(initializeProductImageSwipers, 100);
        return;
      }

      window.initProductImageSwipers(section);
    };

    const getSlidesPerView = () => {
      if (isCartDrawerShowcase) {
        if (window.innerWidth <= 768) return 1.7;
        return 1.87;
      }

      const windowWidth = window.innerWidth;
      if (isCollectionDrops) {
        if (windowWidth < 640) return 1.5;
        if (windowWidth < 1024) return 2.2;
        if (windowWidth < 1280) return 3.2;
        return defaultProductsShownOnDesktop;
      }
      if (windowWidth <= 749) {
        return 1.6;
      } else if (windowWidth <= 1023) {
        return 3;
      } else {
        return defaultProductsShownOnDesktop;
      }
    };

    const notifyAnalyticsBannerTracking = () => {
      document.dispatchEvent(
        new CustomEvent('product-collection-showcase:initialized', {
          detail: { section },
        }),
      );

      if (window.alteregoAnalytics && typeof window.alteregoAnalytics.refreshDynamicBannerTracking === 'function') {
        window.alteregoAnalytics.refreshDynamicBannerTracking(section);
      }
    };

    const initializeMainSwiper = () => {
      if (!mainSwiperElement || typeof Swiper === 'undefined') {
        if (typeof Swiper === 'undefined') {
          setTimeout(() => initializeMainSwiper(), 100);
        }
        return;
      }

      if (mainSwiperInstance) {
        mainSwiperInstance.destroy(true, true);
        mainSwiperInstance = null;
      }

      const slidesPerViewCount = getSlidesPerView();
      const spaceBetweenSlides = isCartDrawerShowcase ? 2 : 0;

      mainSwiperInstance = new Swiper(mainSwiperElement, {
        slidesPerView: isCollectionDrops ? 1.5 : slidesPerViewCount,
        spaceBetween: spaceBetweenSlides,
        navigation: showcaseNavigation,
        slidesPerGroup: 1,
        freeMode: false,
        grabCursor: true,
        width: null,
        autoHeight: false,
        resistance: true,
        resistanceRatio: 0.85,
        speed: 300,
        loop: false,
        allowTouchMove: true,
        ...(isCollectionDrops ? { breakpoints: collectionDropsBreakpoints } : {}),
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
            }
          : false,
        on: {
          init: function () {
            initializeProductImageSwipers();
            updateProgressBar(this);
            notifyAnalyticsBannerTracking();
            if (progressBar) setupProgressBarDrag(progressBar, this);
            if (!wheelListenerAdded && mainSwiperElement) {
              wheelListenerAdded = true;
              mainSwiperElement.addEventListener(
                'wheel',
                function (e) {
                  if (!mainSwiperInstance) return;
                  const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
                  if (!isHorizontal) return;
                  const rawDelta = -e.deltaX;
                  const sensitivity = 18;
                  accumulatedWheelDelta += rawDelta * sensitivity;
                  if (Math.abs(accumulatedWheelDelta) < 0.5) return;
                  e.preventDefault();
                  isWheelScrolling = true;
                  if (wheelRafId == null) {
                    wheelRafId = requestAnimationFrame(function applyWheelFrame() {
                      wheelRafId = null;
                      if (!mainSwiperInstance || Math.abs(accumulatedWheelDelta) < 0.5) {
                        isWheelScrolling = false;
                        return;
                      }
                      const swiperMin = mainSwiperInstance.minTranslate();
                      const swiperMax = mainSwiperInstance.maxTranslate();
                      const minT = Math.min(swiperMin, swiperMax);
                      const maxT = Math.max(swiperMin, swiperMax);
                      const t =
                        typeof mainSwiperInstance.getTranslate === 'function'
                          ? mainSwiperInstance.getTranslate()
                          : mainSwiperInstance.translate;
                      const delta = accumulatedWheelDelta;
                      accumulatedWheelDelta = 0;
                      let newT = t + delta;
                      newT = Math.max(minT, Math.min(maxT, newT));
                      const progress = maxT !== minT ? (newT - maxT) / (minT - maxT) : 0;
                      if (typeof mainSwiperInstance.setTransition === 'function') {
                        mainSwiperInstance.setTransition(0);
                      }
                      mainSwiperInstance.setTranslate(newT);
                      if (typeof mainSwiperInstance.setTransition === 'function') {
                        mainSwiperInstance.setTransition(300);
                      }
                      updateProgressBar(mainSwiperInstance, progress);
                      isWheelScrolling = false;
                    });
                  }
                },
                { passive: false },
              );
            }
          },
          slideChange: function () {
            updateProgressBar(this);
          },
          progress: function () {
            updateProgressBar(this);
          },
          resize: function () {
            if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);
            resizeDebounceTimer = setTimeout(() => {
              if (!isCollectionDrops) {
                this.params.slidesPerView = getSlidesPerView();
                this.update();
              }
              updateProgressBar(this);
            }, 150);
          },
        },
      });
    };

    const loadAndRenderProducts = async () => {
      try {
        const fetchedProducts = await fetchCollectionProducts();
        if (fetchedProducts.length === 0) {
          const skeletonElements = productWrapperElement.querySelectorAll('[data-skeleton]');
          skeletonElements.forEach((skeletonElement) => skeletonElement.remove());
          section.classList.remove('product-collection-showcase--many-products');
          return;
        }

        const colorTagToProductsMap = new Map();
        fetchedProducts.forEach((productItem) => {
          const productColorTag = extractColorTagFromProduct(productItem);
          if (productColorTag && !colorTagToProductsMap.has(productColorTag)) {
            colorTagToProductsMap.set(productColorTag, []);
          }
          if (productColorTag) {
            colorTagToProductsMap.get(productColorTag).push(productItem);
          }
        });

        const colorwayFetchPromises = Array.from(colorTagToProductsMap.keys()).map((colorTag) =>
          fetchColorwayProductsByTag(colorTag),
        );
        await Promise.all(colorwayFetchPromises);

        const colorOptionsFetchPromises = fetchedProducts.map((productItem) => getProductColorOptions(productItem));
        const allProductColorOptions = await Promise.all(colorOptionsFetchPromises);

        let functionCheckRetries = 0;
        while (typeof window.renderProductShowcaseCard !== 'function' && functionCheckRetries < 10) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          functionCheckRetries++;
        }

        if (typeof window.renderProductShowcaseCard !== 'function') {
          const skeletonElements = productWrapperElement.querySelectorAll('[data-skeleton]');
          skeletonElements.forEach((skeletonElement) => skeletonElement.remove());
          section.classList.remove('product-collection-showcase--many-products');
          return;
        }

        const productCardRenderPromises = fetchedProducts.map((productItem, productIndex) =>
          renderProductCardHTML(productItem, allProductColorOptions[productIndex]),
        );
        const renderedCardHTMLs = await Promise.all(productCardRenderPromises);

        const skeletonElements = productWrapperElement.querySelectorAll('[data-skeleton]');
        skeletonElements.forEach((skeletonElement) => skeletonElement.remove());

        productWrapperElement.innerHTML = renderedCardHTMLs.join('');

        /* Toggle pagination visibility on desktop: hide when 4 or fewer products */
        section.classList.toggle('product-collection-showcase--many-products', fetchedProducts.length > 4);

        setTimeout(() => {
          if (typeof window.initCardSkeletons === 'function') {
            window.initCardSkeletons();
          }
        }, 50);

        initializeMainSwiper();

        setTimeout(() => {
          if (mainSwiperInstance) {
            mainSwiperInstance.update();
          }
          initializeProductImageSwipers();
        }, 100);

        const productDataLoadPromises = fetchedProducts.map((productItem) =>
          loadProductAdditionalData(productItem.handle),
        );
        await Promise.all(productDataLoadPromises);

        setTimeout(() => {
          fetchedProducts.forEach((productItem) => {
            const productCardElement = section.querySelector(
              '.product-collection-showcase__card[data-product-handle="' + productItem.handle + '"]',
            );
            if (productCardElement && productCardElement.getAttribute('data-loading') === 'true') {
              const cardImageWrapper = productCardElement.querySelector(
                '.product-collection-showcase__card-image-wrapper',
              );
              const firstProductImage = cardImageWrapper?.querySelector('img');
              if (firstProductImage && firstProductImage.complete && firstProductImage.naturalHeight > 0) {
                productCardElement.setAttribute('data-loading', 'false');
              }
            }
          });
        }, 500);
      } catch (error) {
        const skeletonElements = productWrapperElement.querySelectorAll('[data-skeleton]');
        skeletonElements.forEach((skeletonElement) => skeletonElement.remove());
        section.classList.remove('product-collection-showcase--many-products');
      }
    };

    const loadProductAdditionalData = async (productHandleValue) => {
      try {
        const productDataUrl = `/products/${productHandleValue}?view=review`;
        const productDataResponse = await fetch(productDataUrl);
        const productDataHTML = await productDataResponse.text();
        const temporaryDiv = document.createElement('div');
        temporaryDiv.innerHTML = productDataHTML;

        const customDisplayTextElement = temporaryDiv.querySelector('.custom_display_text');
        const customTitleContainer = section.querySelector(
          `.product-collection-showcase__card[data-product-handle="${productHandleValue}"] .product-collection-showcase__card-custom-title`,
        );

        if (customDisplayTextElement && customTitleContainer) {
          const clonedTextElement = customDisplayTextElement.cloneNode(true);
          if (clonedTextElement.textContent) {
            if (typeof theme !== 'undefined' && theme.utils && theme.utils.toProperCase) {
              clonedTextElement.textContent = theme.utils.toProperCase(clonedTextElement.textContent);
            }
          }
          customTitleContainer.innerHTML = clonedTextElement.outerHTML;
        }

        const reviewBadgeElement = temporaryDiv.querySelector('.jdgm-preview-badge');
        const reviewContainerElement = section.querySelector(
          `.product-collection-showcase__card[data-product-handle="${productHandleValue}"] .product-collection-showcase__card-review`,
        );

        if (reviewBadgeElement && reviewContainerElement) {
          const previousBadgeElement = reviewBadgeElement.querySelector('.jdgm-prev-badge');
          const totalReviewCount = previousBadgeElement
            ? parseInt(previousBadgeElement.getAttribute('data-number-of-reviews') || '0', 10)
            : 0;

          if (totalReviewCount > 0) {
            reviewContainerElement.innerHTML = reviewBadgeElement.outerHTML;

            const reviewBadgeTextElement = reviewContainerElement.querySelector('.jdgm-prev-badge__text');
            if (reviewBadgeTextElement) {
              const reviewText = reviewBadgeTextElement.textContent.trim();
              const reviewCountMatch = reviewText.match(/(\d+)\s+review(s)?/i);
              if (reviewCountMatch) {
                const reviewNumber = reviewCountMatch[1];
                reviewBadgeTextElement.textContent = `(${reviewNumber})`;
              }
            }

            if (typeof window.jdgm !== 'undefined' && window.jdgm.loadWidgets) {
              window.jdgm.loadWidgets();
            }
          } else {
            const reviewWrapperElement = reviewContainerElement?.closest(
              '.product-collection-showcase__card-review-wrapper',
            );
            if (reviewWrapperElement) {
              reviewWrapperElement.style.display = 'none';
            }
          }
        }
        document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
      } catch (error) {}
    };

    const handleProductCardClick = (clickEvent) => {
      const addToCartButton = clickEvent.target.closest('.product-collection-showcase__card-add-to-cart');
      const wishlistButton = clickEvent.target.closest(
        '.product-collection-showcase__card-wishlist, .wishlist-engine, .wishlist-engine-button',
      );
      const imageArrow = clickEvent.target.closest('.product-collection-showcase__card-image-arrow');
      const colorSwatch = clickEvent.target.closest('.product-collection-showcase__card-color-swatch');
      const productCard = clickEvent.target.closest('.product-collection-showcase__card');

      if (addToCartButton || wishlistButton || imageArrow || colorSwatch) {
        return;
      }

      if (productCard) {
        const productHandle = productCard.getAttribute('data-product-handle');
        if (productHandle) {
          window.location.href = `/products/${productHandle}`;
        }
      }
    };

    section.addEventListener('click', (clickEvent) => {
      const addToCartButton = clickEvent.target.closest('.product-collection-showcase__card-add-to-cart');
      if (!addToCartButton || addToCartButton.disabled) return;

      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      clickEvent.stopImmediatePropagation();

      const productHandleValue = addToCartButton.dataset.productHandle;
      const productIdValue = addToCartButton.getAttribute('data-product-id');

      if (!productHandleValue) return;

      if (typeof window.openQuickAddModalProductCards === 'function') {
        window.openQuickAddModalProductCards(productHandleValue, productIdValue, null, addToCartButton);
      } else {
        window.location.href = '/products/' + productHandleValue;
      }
    });

    section.addEventListener('click', handleProductCardClick);

    const sizeGuideModalCloseHandlers = {
      overlayClick: null,
      escapeKey: null,
    };

    const closeSizeGuideModal = () => {
      const toolTipElement = document.getElementById('ProductCollectionSizeGuide-' + section.dataset.sectionId);

      if (!toolTipElement) {
        return;
      }

      toolTipElement.removeAttribute('data-tool-tip-open');

      if (window.theme && window.theme.a11y && window.theme.a11y.unlockMobileScrolling) {
        window.theme.a11y.unlockMobileScrolling();
      }
      document.documentElement.classList.remove('modal-open');
      document.documentElement.classList.remove('lock-scroll');
      document.body.classList.remove('overflow-hidden');

      if (sizeGuideModalCloseHandlers.overlayClick) {
        document.removeEventListener('click', sizeGuideModalCloseHandlers.overlayClick);
        sizeGuideModalCloseHandlers.overlayClick = null;
      }

      if (sizeGuideModalCloseHandlers.escapeKey) {
        document.removeEventListener('keydown', sizeGuideModalCloseHandlers.escapeKey);
        sizeGuideModalCloseHandlers.escapeKey = null;
      }
    };

    const showSizeGuideModal = (modalType) => {
      const toolTipElement = document.getElementById('ProductCollectionSizeGuide-' + section.dataset.sectionId);

      if (!toolTipElement) {
        return;
      }

      const toolTipContent = toolTipElement.querySelector('.tool-tip__content');
      const toolTipInner = toolTipElement.querySelector('.tool-tip__inner');
      const closeButton = toolTipElement.querySelector('.tool-tip__close');

      if (toolTipContent) {
        const toolTipTrigger = toolTipContent.querySelector('tool-tip-trigger');
        if (toolTipTrigger) {
          const triggerContent = toolTipTrigger.querySelector('.tool-tip-trigger__content');
          if (triggerContent) {
            const modalContent = triggerContent.querySelector('.size-guide-modal');
            if (modalContent) {
              const clonedModal = modalContent.cloneNode(true);

              const overlay = clonedModal.querySelector('.size-guide-overlay');
              const container = clonedModal.querySelector('.size-guide-container');

              if (overlay) {
                overlay.removeAttribute('x-show');
                overlay.removeAttribute('@click');
                overlay.removeAttribute('x-transition:enter');
                overlay.removeAttribute('x-transition:enter-start');
                overlay.removeAttribute('x-transition:enter-end');
                overlay.removeAttribute('x-transition:leave');
                overlay.removeAttribute('x-transition:leave-start');
                overlay.removeAttribute('x-transition:leave-end');
              }

              if (container) {
                container.removeAttribute('x-show');
                container.removeAttribute('x-transition:enter');
                container.removeAttribute('x-transition:enter-start');
                container.removeAttribute('x-transition:enter-end');
                container.removeAttribute('x-transition:leave');
                container.removeAttribute('x-transition:leave-start');
                container.removeAttribute('x-transition:leave-end');
              }

              clonedModal.removeAttribute('x-cloak');
              clonedModal.removeAttribute('x-data');
              clonedModal.removeAttribute('@show-size-guide.window');
              clonedModal.removeAttribute('@click.outside');
              clonedModal.removeAttribute('x-show');
              clonedModal.removeAttribute('x-effect');
              clonedModal.removeAttribute('x-transition:enter');
              clonedModal.removeAttribute('x-transition:enter-start');
              clonedModal.removeAttribute('x-transition:enter-end');
              clonedModal.removeAttribute('x-transition:leave');
              clonedModal.removeAttribute('x-transition:leave-start');
              clonedModal.removeAttribute('x-transition:leave-end');

              toolTipTrigger.remove();
              toolTipContent.appendChild(clonedModal);

              if (overlay) {
                overlay.style.display = 'block';
                overlay.style.opacity = '1';
                overlay.addEventListener('click', (e) => {
                  e.stopPropagation();
                  closeSizeGuideModal();
                });
              }
            }
          }
        }
      }

      if (closeButton) {
        closeButton.addEventListener('click', (e) => {
          e.stopPropagation();
          closeSizeGuideModal();
        });
      }

      sizeGuideModalCloseHandlers.overlayClick = (e) => {
        if (toolTipElement.getAttribute('data-tool-tip-open') === 'true') {
          if (toolTipInner && !toolTipInner.contains(e.target) && e.target !== closeButton) {
            closeSizeGuideModal();
          }
        }
      };

      document.addEventListener('click', sizeGuideModalCloseHandlers.overlayClick);

      sizeGuideModalCloseHandlers.escapeKey = (e) => {
        if (e.key === 'Escape' && toolTipElement.getAttribute('data-tool-tip-open') === 'true') {
          closeSizeGuideModal();
        }
      };

      document.addEventListener('keydown', sizeGuideModalCloseHandlers.escapeKey);

      if (window.theme && window.theme.a11y && window.theme.a11y.lockMobileScrolling) {
        window.theme.a11y.lockMobileScrolling();
      }
      document.documentElement.classList.add('modal-open');
      document.documentElement.classList.add('lock-scroll');
      document.body.classList.add('overflow-hidden');

      toolTipElement.setAttribute('data-tool-tip-open', 'true');
    };

    const sizeGuideLink = section.querySelector('.product-collection-showcase__size-guide-link');

    if (sizeGuideLink) {
      sizeGuideLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const detectedType = sizeGuideLink.getAttribute('data-size-guide-type');
        showSizeGuideModal(detectedType);
      });
    }

    const initializeSwiperWithSkeleton = () => {
      if (!mainSwiperElement) {
        setTimeout(() => initializeSwiperWithSkeleton(), 50);
        return;
      }

      if (typeof Swiper === 'undefined') {
        setTimeout(() => initializeSwiperWithSkeleton(), 100);
        return;
      }

      if (mainSwiperInstance && productWrapperElement.children.length > 0) {
        return;
      }

      if (mainSwiperInstance) {
        mainSwiperInstance.destroy(true, true);
        mainSwiperInstance = null;
      }

      const slidesPerViewCount = getSlidesPerView();
      const spaceBetweenSlides = isCartDrawerShowcase ? 16 : 0;

      try {
        mainSwiperInstance = new Swiper(mainSwiperElement, {
          slidesPerView: isCollectionDrops ? 1.5 : slidesPerViewCount,
          spaceBetween: spaceBetweenSlides,
          navigation: showcaseNavigation,
          slidesPerGroup: 1,
          freeMode: false,
          grabCursor: true,
          width: null,
          autoHeight: false,
          resistance: true,
          resistanceRatio: 0.85,
          speed: 300,
          loop: false,
          allowTouchMove: false,
          ...(isCollectionDrops ? { breakpoints: collectionDropsBreakpoints } : {}),
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : false,
          on: {
            init: function () {
              updateProgressBar(this);
              if (progressBar) setupProgressBarDrag(progressBar, this);
            },
            slideChange: function () {
              updateProgressBar(this);
            },
            progress: function () {
              updateProgressBar(this);
            },
          },
        });

        setTimeout(() => {
          if (mainSwiperInstance) {
            mainSwiperInstance.update();
          }
        }, 50);
      } catch (error) {
        setTimeout(() => initializeSwiperWithSkeleton(), 200);
      }
    };

    const initializeSkeletonState = () => {
      if (!mainSwiperElement || !productWrapperElement) {
        setTimeout(() => initializeSkeletonState(), 50);
        return;
      }

      const skeletonElements = productWrapperElement.querySelectorAll('[data-skeleton]');
      if (skeletonElements.length === 0) {
        return;
      }

      initializeSwiperWithSkeleton();
    };

    initializeSkeletonState();

    // Cart-drawer YMAL: fetch immediately so cards are ready when the empty drawer opens.
    // Other showcases keep a short delay to avoid competing with above-the-fold work.
    const productLoadDelay = isCartDrawerShowcase ? 0 : 300;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        if (!mainSwiperInstance) {
          initializeSkeletonState();
        }
        setTimeout(function () {
          loadAndRenderProducts();
        }, productLoadDelay);
      });
    } else {
      setTimeout(function () {
        loadAndRenderProducts();
      }, productLoadDelay);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-product-collection-showcase-section]').forEach(function (section) {
      if (section.dataset.aeShowcaseInited === 'true') return;
      section.dataset.aeShowcaseInited = 'true';
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Drawer was display:none during init — refresh Swiper layout when empty state shows.
  document.addEventListener('cart-drawer:empty-shown', function () {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || !drawer.classList.contains('drawer--is-open')) return;
    if (!drawer.classList.contains('is-empty')) return;

    document
      .querySelectorAll(
        '.product-collection-showcase--cart-drawer[data-product-collection-showcase-section]',
      )
      .forEach(function (section) {
        section.querySelectorAll('[data-product-collection-swiper]').forEach(function (swiperEl) {
          if (swiperEl.swiper) {
            try {
              swiperEl.swiper.update();
            } catch (error) {
              /* ignore */
            }
          }
        });
      });
  });

  document.addEventListener('shopify:section:load', function (e) {
    const section = document.querySelector(
      '[data-product-collection-showcase-section][data-section-id="' + e.detail.sectionId + '"]',
    );
    if (section && section.dataset.aeShowcaseInited !== 'true') {
      section.dataset.aeShowcaseInited = 'true';
      initSection(section);
    }
  });
})();